import React, { forwardRef, HTMLAttributes, useCallback, useEffect, useRef, useState, createContext, useId } from 'react'
import { createPortal } from 'react-dom'
import { Transition } from 'react-transition-group'
import classNames from 'classnames'
import { useForkedRef } from '@evertel/hooks'
import { Backdrop } from '../Backdrop'
import { modalStackMap } from '../modal/Modal'

interface SlidePanelContextProps {
    visible?: boolean
    handleClose: () => void
    headerId: string
    contentId: string
}

export const SlidePanelContext = createContext({} as SlidePanelContextProps)

export interface SlidePanelProps extends HTMLAttributes<HTMLDivElement> {
    backdrop?: boolean|'static'|'clear'
    className?: string
    /** Closes the SlidePanel when escape key is pressed. */
    keyboard?: boolean
    onHide?: () => void
    onShow?: () => void
    onShowComplete?: () => void
    onHideComplete?: () => void
    placement: 'start'|'end'|'top'|'bottom'
    /** Generates panel using createPortal. */
    portal?: boolean
    /** Responsive slidepanel property hide content outside the viewport from a specified breakpoint and down. */
    responsive?: boolean|'sm'|'md'|'lg'|'xl'|'xxl'
    /** Allow body scrolling while slidepanel is open */
    scroll?: boolean
    visible?: boolean
    embed?: boolean
    /** Role of the panel - complementary for supplementary content, navigation for menus */
    role?: 'complementary'|'navigation'|'region'
}

const SlidePanel = forwardRef<HTMLDivElement, SlidePanelProps>(({
    children,
    backdrop = true,
    className,
    keyboard = true,
    onHide,
    onShow,
    onShowComplete,
    onHideComplete,
    placement,
    portal = false,
    responsive = true,
    scroll = false,
    visible = false,
    embed = false,
    role = 'complementary',
    ...otherProps
}, ref) => {

    const [_visible, setVisible] = useState<boolean>(visible)
    const slidePanelRef = useRef<HTMLDivElement>(null)
    const forkedRef = useForkedRef(ref, slidePanelRef)
    const previouslyFocusedElement = useRef<HTMLElement | null>(null)

    // Generate unique IDs for aria attributes
    const headerId = useId()
    const contentId = useId()

    useEffect(() => {
        setVisible(visible)
    }, [visible])

    useEffect(() => {
        if (_visible) {
            // Store the currently focused element
            previouslyFocusedElement.current = document.activeElement as HTMLElement

            if (!scroll) {
                document.body.style.overflow = 'hidden'
                document.body.style.paddingRight = '0px'
            }
        } else {
            if (!scroll) {
                document.body.style.removeProperty('overflow')
                document.body.style.removeProperty('padding-right')
            }

            // Return focus to the previously focused element
            if (previouslyFocusedElement.current && typeof previouslyFocusedElement.current.focus === 'function') {
                previouslyFocusedElement.current.focus()
            }
        }
    }, [_visible])

    const getTransitionClass = (state: string) => {
        return state === 'entering'
            ? 'showing'
            : state === 'entered'
                ? 'show'
                : state === 'exiting'
                    ? 'show hiding'
                    : ''
    }

    const _className = classNames(
        {
            [`slidepanel${typeof responsive !== 'boolean' ? '-' + responsive : ''}`]: responsive,
            [`slidepanel-${placement}`]: placement,
            embed: embed
        },
        className
    )

    const handleClose = () => {
        setVisible(false)
        onHide?.()
    }

    const handleBackdropDismiss = () => {
        if (backdrop !== 'static') {
            handleClose()
        }
    }

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape' && keyboard) {
            // TODO: better way to do this?
            if (!modalStackMap.size) {
                handleClose()
            }
        }
    }, [keyboard])

    useEffect(() => {
        if (_visible) {
            document.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [_visible, handleKeyDown])

    const onEntered = () => {
        slidePanelRef.current?.focus()
        onShowComplete?.()
    }

    const contextValue = {
        visible: _visible,
        handleClose,
        headerId,
        contentId
    }

    const slidepanel = (ref: React.Ref<HTMLDivElement>, state: string) => {
        return (
            <SlidePanelContext.Provider value={contextValue}>
                <div
                    ref={ref}
                    className={classNames(_className, getTransitionClass(state))}
                    role={role}
                    aria-labelledby={headerId}
                    aria-expanded={_visible}
                    aria-controls={contentId}
                    tabIndex={-1}
                    // onKeyDown={handleKeyDown}
                    {...otherProps}
                >
                    {children}
                </div>
            </SlidePanelContext.Provider>
        )
    }

    return (
        <>
            <Transition
                in={_visible}
                nodeRef={slidePanelRef}
                onEnter={onShow}
                onEntered={onEntered}
                onExit={onHide}
                {...(onHideComplete) && {onExited: onHideComplete}}
                timeout={150}
            >
                {(state) => {
                    return typeof window !== 'undefined' && portal
                        ? createPortal(slidepanel(forkedRef, state), document.body)
                        : slidepanel(forkedRef, state)
                }}
            </Transition>
            {(typeof window !== 'undefined' && portal)
                ? (backdrop) &&
                    createPortal(
                        <Backdrop
                            className={classNames('slidepanel-backdrop', {'backdrop-clear': backdrop === 'clear'})}
                            onClick={handleBackdropDismiss}
                            visible={_visible}
                        />,
                        document.body
                    )
                : (backdrop) && (
                    <Backdrop
                        className={classNames('slidepanel-backdrop', {'backdrop-clear': backdrop === 'clear'})}
                        onClick={handleBackdropDismiss}
                        visible={_visible}
                    />
                )}
        </>
    )
})

SlidePanel.displayName = 'SlidePanel'

export { SlidePanel }
